import { useEffect, useRef, useState } from 'react'
import type { RetainedAccountCopy } from '../data/workspaceInventory'
import type { AuthUser } from './authProvider'
import { authorizeRetainedWorkspaceRemoval, finishPendingWorkspaceRemoval } from '../data/workspaceRemoval'

interface Props {
  copies: RetainedAccountCopy[]
  account: AuthUser
  error?: string | null
  onKeep: () => void
  onCancel: () => void
}
/** Replaces the entire private app; cannot be dismissed by Escape or backdrop click. */
export function AccountDataDecision({ copies, account, onKeep, onCancel, error: parentError }: Props) {
  const heading = useRef<HTMLHeadingElement>(null)
  const [confirmRemoval,setConfirmRemoval] = useState(false)
  const [busy,setBusy] = useState(false)
  const [error,setError] = useState<string | null>(null)
  useEffect(() => { heading.current?.focus() }, [])
  const remove = async () => {
    setBusy(true); setError(null)
    try {
      for (const copy of copies) {
        authorizeRetainedWorkspaceRemoval(copy)
        await finishPendingWorkspaceRemoval(copy.scope)
      }
      onKeep()
    } catch { setError('Removal could not finish. The previous account remains hidden. Close other miniMany tabs and retry, or keep the remaining copies separate.'); setBusy(false) }
  }
  return <main className="workspace-boundary" aria-labelledby="account-data-heading"><section className="workspace-boundary__panel">
    <p className="workspace-boundary__brand">miniMany</p>
    <h1 ref={heading} tabIndex={-1} id="account-data-heading">{confirmRemoval ? 'Remove the previous offline content?' : 'Choose what happens to the previous account’s data.'}</h1>
    <dl className="workspace-boundary__accounts">
      <div>
        <dt>Offline data on this device belongs to</dt>
        {copies.map(copy => <dd key={copy.databaseName}>
          <strong>{copy.identity?.email ?? 'Email unavailable for this saved account'}</strong>
          {copy.identity?.name && copy.identity.name !== copy.identity.email && <span>{copy.identity.name}</span>}
          {!copy.identity?.email && <span>Account ID: {copy.owner}</span>}
        </dd>)}
      </div>
      <div>
        <dt>You’re signing in with</dt>
        <dd>
          <strong>{account.email ?? 'Email unavailable for the selected account'}</strong>
          {account.name !== account.email && <span>{account.name}</span>}
          {!account.email && <span>Account ID: {account.id}</span>}
        </dd>
      </div>
    </dl>
    <p>These are different accounts. The previous offline content stays hidden and will never be merged into the account you selected.</p>
    <p>Choose whether to keep the previous offline {copies.length === 1 ? 'copy' : 'copies'} separate or remove {copies.length === 1 ? 'it' : 'them'} from this device before continuing.</p>
    <p>Keeping a copy preserves unsynced work. To review or export it, cancel and sign in to its original account. Keeping data separate is not encryption against someone with access to this browser profile.</p>
    {confirmRemoval && <div className="sign-out-dialog__warning" role="alert"><strong>Unsynced notes, drafts, images and saved collections will be permanently lost.</strong><p>Only these other accounts’ offline copies in this installation are removed. Synced cloud data and downloaded files remain. If you need a recovery copy, cancel and sign in to the original account first.</p></div>}
    {(error || parentError) && <p role="alert">{error || parentError}</p>}
    <div className="workspace-boundary__actions">
      <button className="btn" disabled={busy} onClick={onCancel}>Cancel sign-in</button>
      <button className="btn btn-primary" disabled={busy} onClick={onKeep}>Keep separate &amp; continue</button>
      <button className="btn btn-error btn-outline" disabled={busy} onClick={() => confirmRemoval ? void remove() : setConfirmRemoval(true)}>{confirmRemoval ? 'Permanently remove & continue' : 'Remove offline copies…'}</button>
    </div>
    {busy && <p role="status">Removing the selected offline copies…</p>}
  </section></main>
}
